import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface DogLoaderProps {
  message?: string;
  variant?: 'default' | 'compact' | 'minimal';
}

const dogMessages = [
  "Fetching your data...",
  "Sniffing out the best forecast...",
  "Chasing those numbers...",
  "Digging up insights...",
  "Crunching data...",
  "Tail-wagging calculations...",
  "Paws-ing to think...",
  "Retrieving results...",
  "Herding your metrics...",
  "Almost there...",
];

// Paw print positions for walking animation
const pawPrints = [
  { x: 0, delay: 0, rotation: -15 },
  { x: 35, delay: 0.15, rotation: 15 },
  { x: 70, delay: 0.3, rotation: -15 },
  { x: 105, delay: 0.45, rotation: 15 },
  { x: 140, delay: 0.6, rotation: -15 },
  { x: 175, delay: 0.75, rotation: 15 },
  { x: 210, delay: 0.9, rotation: -15 },
];

export default function DogLoader({ message, variant = 'default' }: DogLoaderProps) {
  const [currentMessage, setCurrentMessage] = useState(message || dogMessages[0]);

  useEffect(() => {
    if (message) return; // Don't rotate if a custom message is provided

    const interval = setInterval(() => {
      setCurrentMessage(dogMessages[Math.floor(Math.random() * dogMessages.length)]);
    }, 2500);

    return () => clearInterval(interval);
  }, [message]);

  // Minimal variant - just a single pulsing paw, no text
  if (variant === 'minimal') {
    return (
      <div className="flex items-center justify-center py-4">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="text-2xl text-purple-500"
        >
          🐾
        </motion.div>
      </div>
    );
  }

  // Compact variant - single paw with message inline
  if (variant === 'compact') {
    return (
      <div className="flex items-center space-x-3">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="text-xl text-purple-500"
        >
          🐾
        </motion.div>
        <span className="text-gray-700 font-medium">{currentMessage}</span>
      </div>
    );
  }

  // Default variant - full paw trail animation
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6">
      {/* Animated paw prints trail */}
      <div className="relative h-16 w-64 mb-8 overflow-hidden">
        <div className="absolute inset-0 flex items-center">
          {pawPrints.map((paw, index) => (
            <motion.div
              key={index}
              className="absolute text-2xl"
              style={{
                left: `${paw.x}px`,
                rotate: `${paw.rotation}deg`,
              }}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{
                opacity: [0, 0.4, 1, 1, 0.4, 0],
                scale: [0.5, 0.8, 1, 1, 0.8, 0.5],
                y: [8, 0, 0, 0, 0, -8],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: paw.delay,
                times: [0, 0.1, 0.2, 0.8, 0.9, 1],
                ease: "easeInOut",
              }}
            >
              <span className="text-purple-400">🐾</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Loading message */}
      <motion.div
        key={currentMessage}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        <p className="text-lg font-semibold text-gray-800 mb-2">{currentMessage}</p>
        <p className="text-sm text-gray-500">This may take a moment...</p>
      </motion.div>

    </div>
  );
}
